import type { OfficeTarget } from "../services/speEditingService";
import { authClient, BEARER_TOKEN_KEY } from "./auth-client";
export const OFFICE_RESUME_KEY = "kmvs-office-resume";
export type OfficeResumeIntent = {
  documentId: number; target: OfficeTarget; userId: string; tenantId: string; expiresAt: number;
  /** Die Anmeldung lief im eigens geöffneten Tab — nur der darf danach durch Office ersetzt werden. */
  ownTab?: boolean;
};
/** Würde die Microsoft-Anmeldung den KMVS-Tab selbst umleiten (und damit offene Eingaben verwerfen)? */
export const officeLoginReplacesApp = (target: OfficeTarget, tab: Window | null): boolean =>
  !(target === "web" && tab && !tab.closed);
/** OAuth bleibt beim Web-Start im zuvor durch den Zielklick reservierten Tab. */
export async function startOfficeMicrosoftLogin(documentId: number, target: OfficeTarget, tab: Window | null): Promise<void> {
  const [session, accounts] = await Promise.all([authClient.getSession(), authClient.listAccounts()]);
  if (session.error || !session.data?.user.id || accounts.error || !Array.isArray(accounts.data))
    throw new Error("Microsoft-Anmeldung konnte nicht gestartet werden. Bitte erneut versuchen.");
  const callback = new URL(window.location.href);
  callback.searchParams.delete("office_auth_error");
  const errorCallback = new URL(callback);
  errorCallback.searchParams.set("office_auth_error", "1");
  const options = { provider: "microsoft" as const, disableRedirect: true, callbackURL: callback.href, errorCallbackURL: errorCallback.href };
  const linked = accounts.data.some(a => a.providerId === "microsoft");
  // Ein fehlendes Konto wird an die bestehende KMVS-Sitzung gebunden.
  const response = linked ? await authClient.signIn.social(options) : await authClient.linkSocial(options);
  if (response.error || !response.data?.url)
    throw new Error("Microsoft-Anmeldung konnte nicht gestartet werden. Bitte erneut versuchen.");
  const url = new URL(response.data.url);
  if (url.protocol !== "https:" || url.hostname !== "login.microsoftonline.com" || url.username || url.password)
    throw new Error("Microsoft-Anmeldung hat eine ungültige Rückleitung geliefert.");
  const ownTab = !officeLoginReplacesApp(target, tab);
  const intent: OfficeResumeIntent = {
    documentId, target, userId: session.data.user.id,
    tenantId: (session.data.session as { activeOrganizationId?: string | null }).activeOrganizationId || "default",
    expiresAt: Date.now() + 10 * 60 * 1000,
    ownTab,
  };
  const recipient = ownTab ? tab! : window;
  recipient.sessionStorage.setItem(OFFICE_RESUME_KEY, JSON.stringify(intent));
  // Erst nach erfolgreicher Initialisierung entfernen: die neue Cookie-Sitzung
  // darf nach OAuth nicht vom alten Bearer überschattet werden.
  localStorage.removeItem(BEARER_TOKEN_KEY);
  recipient.location.replace(url.href);
}

/** Einmalige Fortsetzung; fremde Konten oder abgebrochene Logins öffnen nichts. */
export function consumeOfficeResume(userId: string, tenantId: string, failed = false): { intent: OfficeResumeIntent } | { error: string } | null {
  const raw = sessionStorage.getItem(OFFICE_RESUME_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(OFFICE_RESUME_KEY);
  let intent: OfficeResumeIntent;
  try { intent = JSON.parse(raw); } catch { return { error: "Die Office-Auswahl konnte nicht wiederhergestellt werden. Bitte das Dokument erneut wählen." }; }
  if (!intent || !Number.isSafeInteger(intent.documentId) || intent.documentId <= 0 ||
      !["web", "desktop"].includes(intent.target) || !Number.isFinite(intent.expiresAt) ||
      (intent.ownTab !== undefined && typeof intent.ownTab !== "boolean"))
    return { error: "Die Office-Auswahl konnte nicht wiederhergestellt werden. Bitte das Dokument erneut wählen." };
  if (failed) return { error: "Die Microsoft-Anmeldung wurde abgebrochen. Bitte das Dokument erneut wählen." };
  if (intent.expiresAt <= Date.now()) return { error: "Die Office-Auswahl ist abgelaufen. Bitte das Dokument erneut wählen." };
  if (intent.userId !== userId || intent.tenantId !== tenantId)
    return { error: "Das angemeldete Konto gehört nicht zur vorgemerkten Bearbeitung. Bitte das Dokument erneut wählen." };
  return { intent };
}
